import { supplierApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { ServiceResponseI, DataI } from "../../interfaces";
import { SupplierI, GetSupplierParamsI } from "@interfaces";
import { AxiosError } from "axios";

const getSuppliers = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetSupplierParamsI): Promise<ServiceResponseI<DataI<SupplierI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());
    if (status !== undefined) params.append("status", String(status));

    const { data } = await supplierApi.get<ServiceResponseI<DataI<SupplierI>>>(
      "",
      {
        params,
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllSuppliers = async (
  clientId?: number | OnErrorEventHandlerNonNull,
): Promise<ServiceResponseI<SupplierI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await supplierApi.get<ServiceResponseI<SupplierI[]>>(
      "/all-no-pagination",
      { params: { clientId }, headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const supplierById = async (
  id: string,
): Promise<ServiceResponseI<SupplierI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await supplierApi.get<ServiceResponseI<SupplierI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const supplierPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<{ message: string; id: string }>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await supplierApi.post<
      ServiceResponseI<{ message: string; id: string }>
    >("", payload, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const supplierPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await supplierApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const supplierDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await supplierApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useSuppliers = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
}: GetSupplierParamsI) => {
  const suppliersQuery = useQuery(
    ["suppliers", { pagination, sorting, globalFilter, clientId, status }],
    () => getSuppliers({ pagination, sorting, globalFilter, clientId, status }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { suppliersQuery };
};
